/**
 * obKit — Rêve OS · Onboarding shared UI kit (warm "Reve OS" editorial system).
 *
 * One import for every onboarding surface built against the claude.ai/design
 * `Rêve OS · Onboarding.html` artboards: the agent-facing Agent Information Form
 * (RW-990) + self-status page (RW-989), and the admin access (RW-987), nudges
 * (RW-985), email (RW-986), swag (RW-988), and Template Builder (RW-998) surfaces.
 *
 * It translates the design's `RP.*` tokens onto the live theme.js values (the
 * warm-neutral palette already shipped for Coming Soon / Groups) and the design's
 * `RPIcon`/`RPPill`/`RPAvatar`/`RPBtn` primitives onto small, dependency-free
 * components. Icons reuse the project's Lucide set (feedIcons.js) plus a few
 * onboarding extras defined here — no new node_modules.
 *
 * Inline-hex / TDZ-safe: this module reads from theme.js `colors`/`fonts` and
 * exposes a flat `OB` token object; do not import OB into a module-level const in
 * a way that creates a circular TDZ (see sharedConstants.js note).
 */
import React from 'react';
import { colors, fonts } from '../theme';
import { normalizeRoles } from '../../lib/roles';
import { isAgentItem } from '../../utils/onboardingPermissions';
import {
  IconCheck, IconLock, IconShield, IconUpload, IconFileText, IconChevronRight,
  IconChevronDown, IconPlus, IconClock, IconBell, IconAward, IconSparkles,
  IconAlert, IconUsers, IconCalendar, IconPencil, IconExternal,
} from '../feedIcons';
import { usablePhotoURL } from '../../lib/avatarPhoto';

/* ───────── Tokens (RP.* → theme.colors/fonts) ───────── */
export const OB = {
  // ink + text
  ink: colors.text,            // #2A2622 near-black editorial ink
  text: colors.text,
  textSoft: colors.textSoft,
  muted: colors.muted,
  mute2: colors.mute2,
  // surfaces
  bg: colors.canvas,           // warm page background
  panel: colors.panel,
  panelDeep: colors.panelDeep,
  white: colors.white,
  line: colors.line,
  line2: colors.line2,
  // coral (primary / agent)
  coral: colors.coral,
  coralDark: colors.coralDark,
  coralInk: colors.coralInk, // RW-2960: AA text on light surfaces (== colors.coralInk); coralDark stays for fills/hover
  coralSoft: colors.coralSoft,
  // moss (done / success)
  moss: colors.moss,
  mossSoft: colors.mossSoft,
  mossMid: colors.mossMid,
  ok: colors.moss,
  // indigo (automation / finance / "pre-filled")
  indigo: colors.indigo,
  indigoSoft: colors.indigoSoft,
  indigoDeep: colors.indigoDeep,
  infoBg: colors.indigoSoft,
  infoText: colors.indigoDeep,
  // gold / purple / blue accents
  gold: colors.gold,
  goldSoft: colors.goldSoft,
  purple: colors.purple,
  purpleLight: colors.purpleLight,
  blue: colors.blue,
  // status
  warnBg: colors.goldSoft,
  warnText: '#8A6D1F',
  errBg: '#FCE9E6',
  errText: colors.errorRed,
  // type
  font: fonts.body,            // Jost
  serif: fonts.serif,          // Fraunces
};

/* ───────── Icons ───────── */
/* A tiny local base for the few onboarding glyphs feedIcons doesn't ship. */
function Glyph({ size = 18, color = 'currentColor', strokeWidth = 1.5, children, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false" style={{ display: 'block', flexShrink: 0 }} {...rest}>
      {children}
    </svg>
  );
}
const IconBolt = (p) => <Glyph {...p}><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" /></Glyph>;
const IconArrowRight = (p) => <Glyph {...p}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></Glyph>;
const IconMail = (p) => <Glyph {...p}><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></Glyph>;
const IconSend = (p) => <Glyph {...p}><path d="M14.54 2.4 4 6.32a1 1 0 0 0-.05 1.86l4.69 1.93a1 1 0 0 1 .55.55l1.93 4.69a1 1 0 0 0 1.86-.05l3.92-10.54a1 1 0 0 0-1.27-1.27Z" /><path d="m21.5 2.5-12 12" /></Glyph>;
const IconPackage = (p) => <Glyph {...p}><path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></Glyph>;
const IconTruck = (p) => <Glyph {...p}><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" /><path d="M15 18H9" /><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62l-3.48-4.35A1 1 0 0 0 17.52 8H14" /><circle cx="17" cy="18" r="2" /><circle cx="7" cy="18" r="2" /></Glyph>;
const IconPhone = (p) => <Glyph {...p}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.74-1.27a2 2 0 0 1 2.11-.45c.74.34 1.53.57 2.34.7A2 2 0 0 1 22 16.92Z" /></Glyph>;
const IconMessage = (p) => <Glyph {...p}><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" /></Glyph>;
const IconClipboard = (p) => <Glyph {...p}><rect width="8" height="4" x="8" y="2" rx="1" ry="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /></Glyph>;
const IconKey = (p) => <Glyph {...p}><circle cx="7.5" cy="15.5" r="5.5" /><path d="m21 2-9.6 9.6" /><path d="m15.5 7.5 3 3L22 7l-3-3" /></Glyph>;
const IconRefresh = (p) => <Glyph {...p}><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></Glyph>;
// RW-1024 — intra-module subnav glyphs (mirror the design's SNV icon names).
const IconHome = (p) => <Glyph {...p}><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /><path d="M9 22V12h6v10" /></Glyph>;
const IconPin = (p) => <Glyph {...p}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></Glyph>;
const IconChart = (p) => <Glyph {...p}><path d="M3 3v18h18" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" /></Glyph>;
const IconDollar = (p) => <Glyph {...p}><line x1="12" x2="12" y1="2" y2="22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></Glyph>;
const IconAt = (p) => <Glyph {...p}><circle cx="12" cy="12" r="4" /><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" /></Glyph>;
const IconSnooze = (p) => <Glyph {...p}><circle cx="12" cy="13" r="8" /><path d="M5 3 2 6" /><path d="m22 6-3-3" /><path d="M12 9v4l2 1.5" /></Glyph>;
const IconArchive = (p) => <Glyph {...p}><rect width="20" height="5" x="2" y="3" rx="1" /><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" /><path d="M10 12h4" /></Glyph>;
// RW-1666 — template-library glyphs.
const IconSearch = (p) => <Glyph {...p}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></Glyph>;
const IconX = (p) => <Glyph {...p}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Glyph>;
const IconCopy = (p) => <Glyph {...p}><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></Glyph>;
const IconList = (p) => <Glyph {...p}><line x1="8" x2="21" y1="6" y2="6" /><line x1="8" x2="21" y1="12" y2="12" /><line x1="8" x2="21" y1="18" y2="18" /><line x1="3" x2="3.01" y1="6" y2="6" /><line x1="3" x2="3.01" y1="12" y2="12" /><line x1="3" x2="3.01" y1="18" y2="18" /></Glyph>;
const IconStar = (p) => <Glyph {...p}><path d="M12 2l3 7 8 .7-6 5 2 8-7-4-7 4 2-8-6-5 8-.7z" /></Glyph>;
const IconPlay = (p) => <Glyph {...p}><path d="m6 4 13 8-13 8Z" /></Glyph>;
// RW-2430 — the task drawer's Reply affordance.
const IconReply = (p) => <Glyph {...p}><path d="M9 7 4 12l5 5" /><path d="M4 12h10a6 6 0 0 1 6 6v1" /></Glyph>;
// RW-2429 — the task drawer's hire-visible badge (eye ⇄ lock).
const IconEye = (p) => <Glyph {...p}><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></Glyph>;

/** Icon name → component. Mirrors the design's RPIcon names so artboard code
 *  translates mechanically: <OBIcon name="check" .../>. */
const ICONS = {
  check: IconCheck, lock: IconLock, shield: IconShield, upload: IconUpload,
  clip: IconUpload, file: IconFileText, doc: IconFileText, sign: IconPencil,
  chev: IconChevronDown, chevR: IconChevronRight, arrR: IconArrowRight,
  bolt: IconBolt, plus: IconPlus, clock: IconClock, bell: IconBell,
  award: IconAward, sparkles: IconSparkles, alert: IconAlert, users: IconUsers,
  calendar: IconCalendar, pencil: IconPencil, external: IconExternal,
  mail: IconMail, send: IconSend, package: IconPackage, truck: IconTruck,
  phone: IconPhone, message: IconMessage, clipboard: IconClipboard, key: IconKey,
  refresh: IconRefresh,
  // RW-1024 subnav glyphs
  home: IconHome, pin: IconPin, chart: IconChart, dollar: IconDollar, at: IconAt,
  snooze: IconSnooze, archive: IconArchive,
  // RW-1666 template-library glyphs (+ aliases matching the design's RPIcon names)
  search: IconSearch, x: IconX, copy: IconCopy, list: IconList, star: IconStar,
  play: IconPlay, house: IconHome, warn: IconAlert, sparkle: IconSparkles,
  eye: IconEye, reply: IconReply,
};

export function OBIcon({ name, size = 16, color = OB.muted, ...rest }) {
  const C = ICONS[name] || IconFileText;
  return <C size={size} color={color} {...rest} />;
}

/* ───────── Primitives ───────── */

/** Uppercase tracked eyebrow label. */
export const OBEyebrow = ({ color = OB.muted, children, style }) => (
  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color, fontFamily: OB.font, ...style }}>
    {children}
  </div>
);

/** Small status pill. */
export const OBPill = ({ bg = OB.panel, color = OB.muted, children, style }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: bg, color, fontFamily: OB.font, whiteSpace: 'nowrap', ...style }}>
    {children}
  </span>
);

/** ab-02 filter-chip row — active = ink bg. The ONE chip vocabulary for staff
 *  surfaces (RW-1724): MyOnboardingWork and the per-hire checklist filters
 *  (RW-792) both render this component, never a local copy.
 *  `chips` = [[id, label], …]; `active` = the selected id. */
export const OBFilterChips = ({ chips = [], active, onSelect, style }) => (
  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', ...style }}>
    {chips.map(([id, label]) => {
      const on = active === id;
      return (
        <button
          key={id}
          type="button"
          onClick={() => onSelect && onSelect(id)}
          aria-pressed={on}
          style={{
            fontSize: 11.5, padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
            border: `1px solid ${on ? OB.ink : OB.line}`,
            background: on ? OB.ink : '#fff',
            color: on ? OB.bg : OB.muted,
            fontWeight: on ? 600 : 400, fontFamily: OB.font,
          }}
        >{label}</button>
      );
    })}
  </div>
);

/** "Pre-filled" badge used by the Agent Info Form + provisioning auto items. */
export const OBAutoBadge = ({ label = 'Pre-filled', icon = 'bolt' }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: OB.indigoDeep, background: OB.indigoSoft, padding: '2px 6px', borderRadius: 4, fontFamily: OB.font }}>
    <OBIcon name={icon} size={9} color={OB.indigoDeep} /> {label}
  </span>
);

/** Avatar circle — a real photo when `src` is given (RW-1192), else initials. */
export const OBAvatar = ({ name = '', initials, size = 28, color = OB.coral, src: rawSrc, style }) => {
  const src = usablePhotoURL(rawSrc); // RW-3825: a stock "no photo" image counts as no photo
  const [imgErr, setImgErr] = React.useState(false);
  const text = initials || (name || '')
    .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '—';
  if (src && !imgErr) {
    return (
      <img src={src} alt={name} title={name} onError={() => setImgErr(true)} style={{
        width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, ...style,
      }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: color, color: '#fff',
      display: 'grid', placeItems: 'center', fontSize: size * 0.4, fontWeight: 700,
      fontFamily: OB.font, flexShrink: 0, letterSpacing: '0.02em', ...style,
    }} title={name}>{text}</div>
  );
};

/** Thin progress bar. */
export const OBProgressBar = ({ pct = 0, color = OB.coral, height = 6, track = OB.line2, style }) => (
  <div style={{ height, background: track, borderRadius: height / 2, overflow: 'hidden', ...style }}>
    <div style={{ height: '100%', width: `${Math.max(0, Math.min(100, pct))}%`, background: color, borderRadius: height / 2, transition: 'width .3s ease' }} />
  </div>
);

/** Button — primary (coral) / secondary (hairline) / ghost. */
export const OBBtn = ({ variant = 'secondary', size = 'md', children, disabled, style, ...rest }) => {
  const pad = size === 'lg' ? '11px 18px' : size === 'sm' ? '5px 10px' : '8px 14px';
  const fs = size === 'lg' ? 14 : size === 'sm' ? 12 : 13;
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
    padding: pad, fontSize: fs, fontWeight: 600, borderRadius: 9, cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: OB.font, border: '1.5px solid transparent', opacity: disabled ? 0.55 : 1, lineHeight: 1.1,
  };
  const variants = {
    primary: { background: OB.coral, color: '#fff', borderColor: OB.coral },
    secondary: { background: '#fff', color: OB.ink, borderColor: OB.line },
    ghost: { background: 'transparent', color: OB.muted, borderColor: 'transparent' },
    danger: { background: '#fff', color: OB.errText, borderColor: '#F3C7C0' },
  };
  return <button disabled={disabled} style={{ ...base, ...variants[variant], ...style }} {...rest}>{children}</button>;
};

/** Numbered / titled section card (Agent Info Form sections + admin panels). */
export const OBCard = ({ n, title, desc, pii, headerRight, children, style }) => (
  <div style={{ background: '#fff', border: `1px solid ${OB.line}`, borderRadius: 14, overflow: 'hidden', marginBottom: 16, fontFamily: OB.font, ...style }}>
    {(title || n) && (
      <div style={{ padding: '15px 22px', borderBottom: `1px solid ${OB.line2}`, display: 'flex', alignItems: 'center', gap: 12, background: OB.panel }}>
        {n != null && (
          <div style={{ width: 26, height: 26, borderRadius: '50%', background: OB.ink, color: '#fff', fontSize: 12, fontWeight: 700, display: 'grid', placeItems: 'center', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{n}</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: OB.serif, fontSize: 17, fontWeight: 500, color: OB.ink, letterSpacing: '-0.01em' }}>{title}</div>
          {desc && <div style={{ fontSize: 11.5, color: OB.textSoft, marginTop: 1 }}>{desc}</div>}
        </div>
        {pii && (
          <OBPill bg={OB.indigoSoft} color={OB.indigoDeep} style={{ fontSize: 10, padding: '4px 9px' }}>
            <OBIcon name="lock" size={11} color={OB.indigoDeep} /> Private · Finance &amp; admin only
          </OBPill>
        )}
        {headerRight}
      </div>
    )}
    <div style={{ padding: '20px 22px' }}>{children}</div>
  </div>
);

/* ───────── Staff role → avatar mapping (RW-998, demoted in RW-1685) ─────────
   Maps the ASSIGNEE_ROLES tokens onto named Rêve staff. FALLBACK ONLY since
   RW-1685: staffForToken() resolves the LIVE user holding the role token
   first, so a staff change shows up without a code edit — this map is what
   renders when no live roster is loaded (or nobody holds the role). Known
   stale: office_coordinator has been Ellen (not Alex) since the v3 plan §0.2
   audit — kept as-was to make plain that live data, not this map, is the
   source of truth. */
export const OB_STAFF = {
  agent_services:    { name: 'Tammy Srinivas',  role: 'Agent Services',     initials: 'TS', color: OB.coral },
  admin:             { name: 'Victoria Lacayo',  role: 'Finance / Admin',    initials: 'VL', color: OB.indigo },
  office_coordinator:{ name: 'Alex Gogol',       role: 'Office Coordinator', initials: 'AG', color: OB.gold },
  marketing:         { name: 'Jennyfer Real',    role: 'Marketing',          initials: 'JR', color: OB.purple },
  relo_services:     { name: 'Renée Eads',       role: 'Relocation',         initials: 'RE', color: OB.moss },
  broker:            { name: 'Deidre Hurst',     role: 'Managing Broker',    initials: 'DH', color: OB.blue },
};

/**
 * RW-1192/1194: resolve the onboarding concierge to a LIVE user (the
 * agent_services holder — Tammy) so surfaces can show a real photo and open a
 * DM. agent_services is a platform role (not office-scoped) → effectively one
 * person; if more than one, prefer a name match to OB_STAFF, else the first
 * active one. Returns the user object ({ uid, displayName, photoURL, ... }) or
 * null when none is loaded (callers fall back to the static OB_STAFF record).
 */
export function resolveConciergeUser(allUsers) {
  const matches = (allUsers || []).filter((u) => u && u.isActive !== false
    && normalizeRoles(u.roles != null ? u.roles : u.role).includes('agent_services'));
  if (!matches.length) return null;
  return matches.find((u) => u.displayName === OB_STAFF.agent_services.name) || matches[0];
}

/* ───────── RW-1237: ONE onboarding phase vocabulary ─────────
   The checklist, SelfStatus, SuccessScreen, and the LREC task detail each
   carried their own phase copy ("Paperwork" / "In-person day" / "Go live" vs
   the checklist's friendly names), so the same journey read differently on
   every surface. This map is the single source — keyed by the live seed phase
   ids (seedOnboardingTemplates PHASE_META: phase_1_initial / phase_2_meeting /
   phase_3_post) with a 1|2|3 index fallback for surfaces that resolve items to
   a numeric phase (SelfStatus/SuccessScreen phaseOf). The checklist's wording
   is canonical. */
export const OB_PHASE_LABELS = {
  phase_1_initial: 'Before we meet',
  phase_2_meeting: 'In person',
  phase_3_post: 'Week one',
  1: 'Before we meet',
  2: 'In person',
  3: 'Week one',
};

/** Deck `.p-*` pill tint families — the ONE status→tint source for the warm
 *  redesign surfaces (RW-1724; consumers: MyOnboardingWork rows, the task
 *  drawer's status pills). sharedConstants' hex status map is the LEGACY
 *  palette for pre-redesign surfaces — a different system, not a duplicate. */
export const OB_STATUS_TINT = {
  pending: { bg: OB.line2, fg: OB.muted },
  in_progress: { bg: OB.warnBg, fg: OB.warnText },
  blocked: { bg: OB.errBg, fg: '#9A392B' },
  needs_review: { bg: OB.indigoSoft, fg: OB.indigoDeep },
  complete: { bg: OB.mossSoft, fg: OB.moss },
  waived: { bg: OB.purpleLight, fg: OB.purple },
};

/* ───────── RW-1238: real scheduling — shared due-date/day helpers ─────────
   The checklist's old due/overdue pill measured `item.autoNudgeDays` against
   `instance.startedAt` — but autoNudgeDays is the REMINDER CADENCE (consumed by
   functions-scheduled/scheduled/onboardingNudge.js), seeded 3 on every item, so
   on day 4 all 62 remaining items read "Overdue" at once. These helpers derive
   display scheduling from the item's real due offset instead:

     dueDate = (instance.startedAt || createdAt) + (dueOffsetDays ?? dueOffset) days

   Items with NO explicit due offset return null — no due date, never "Overdue".
   Do NOT fall back to autoNudgeDays for display; leave the nudge cron's
   semantics alone.

   RW-1212: ALL day math here is pinned to America/Chicago — calendar days are
   compared via Intl.DateTimeFormat parts, never raw ms/86400000 offsets, so the
   countdown reads the same for every viewer regardless of device timezone. */
const CT_TZ = 'America/Chicago';
const ctPartsFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: CT_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
});

/** Calendar-day parts { year, month, day } of `date` in America/Chicago. */
export function ctDateParts(date) {
  const parts = {};
  ctPartsFmt.formatToParts(date).forEach((p) => { if (p.type !== 'literal') parts[p.type] = Number(p.value); });
  return { year: parts.year, month: parts.month, day: parts.day };
}

/** Whole-day index of the America/Chicago calendar day containing `date`. */
function ctDayIndex(date) {
  const { year, month, day } = ctDateParts(date);
  return Math.round(Date.UTC(year, month - 1, day) / 86400000);
}

/** Signed count of CT calendar days from `from` to `to` (0 = same CT day). */
export function ctCalendarDaysBetween(from, to) {
  return ctDayIndex(to) - ctDayIndex(from);
}

/** End-of-day (23:59:59) in America/Chicago for a 'YYYY-MM-DD' date-input
 *  string, as a real Date instant. Used by both instance-create doors so a
 *  stored targetDate lands on the intended brokerage-timezone day no matter
 *  where the creating admin's laptop thinks it is (RW-1212). */
export function ctEndOfDay(yyyyMmDd) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: CT_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  // Start from the UTC interpretation, then correct by the offset Intl reports
  // for CT at that instant (no tz library; DST transitions happen at 2am so
  // they can't straddle a 23:59:59 target).
  const utcGuess = new Date(`${yyyyMmDd}T23:59:59Z`);
  const p = {};
  dtf.formatToParts(utcGuess).forEach((x) => { if (x.type !== 'literal') p[x.type] = Number(x.value); });
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour === 24 ? 0 : p.hour, p.minute, p.second);
  return new Date(utcGuess.getTime() - (asUtc - utcGuess.getTime()));
}

/** 'YYYY-MM-DD' (date-input value) of `date`'s America/Chicago calendar day. */
export function ctYmd(date) {
  const { year, month, day } = ctDateParts(date);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const obToDate = (ts) => {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  return ts instanceof Date ? ts : null;
};

/** Real due date for a checklist item, or null when the item carries no
 *  explicit due offset. Reads the editor key (dueOffsetDays) first, then the
 *  v1-seed legacy key (dueOffset). Anchor = instance.startedAt, falling back
 *  to createdAt. */
export function obItemDueDate(item, instance) {
  const offset = Number.isFinite(item?.dueOffsetDays)
    ? item.dueOffsetDays
    : (Number.isFinite(item?.dueOffset) ? item.dueOffset : null);
  if (offset == null) return null;
  const anchor = obToDate(instance?.startedAt) || obToDate(instance?.createdAt);
  if (!anchor) return null;
  return new Date(anchor.getTime() + offset * 86400000);
}

/** CT calendar days until an item's due date: 0 = due today, negative =
 *  overdue by |n| days, null = no due date (never overdue). */
export function obItemDaysUntilDue(item, instance, now = new Date()) {
  const due = obItemDueDate(item, instance);
  if (!due) return null;
  return ctCalendarDaysBetween(now, due);
}

/** CT calendar days until the instance's targetDate ("days remaining"), or
 *  null when no targetDate is set. Negative = past the target. */
export function obDaysRemaining(instance, now = new Date()) {
  const target = obToDate(instance?.targetDate);
  if (!target) return null;
  return ctCalendarDaysBetween(now, target);
}

/** "Mon D" label for a due date, rendered on the CT calendar day. */
export function obFormatDueDate(date) {
  return date
    ? date.toLocaleDateString('en-US', { timeZone: CT_TZ, month: 'short', day: 'numeric' })
    : null;
}

/* ───────── RW-3901: ONE "what's on the hire right now" selector ─────────
   The checklist header ("N need you today") and the Status page ("On you ·
   right now") each built their own list, so Status sent the hire to items
   already marked signed (needs_review — waiting on Agent Services, nothing
   left for the hire) and left out overdue optional items the checklist
   flagged. Both surfaces now read these two helpers. */
const OB_NOT_ON_HIRE = new Set(['complete', 'waived', 'needs_review']);

/** The hire's open items — assigned to the agent and not complete / waived /
 *  waiting on Agent Services' review. Most urgent first: overdue (most days
 *  late first), due today, then upcoming by due date, then undated; ties keep
 *  template order. Each entry: { item, daysLeft } (daysLeft per
 *  obItemDaysUntilDue: negative = overdue, null = no due date). */
export function obHireOpenItems(instance, now = new Date()) {
  const items = Array.isArray(instance?.items) ? instance.items : [];
  return items
    .map((item, idx) => ({ item, idx, daysLeft: obItemDaysUntilDue(item, instance, now) }))
    .filter(({ item }) => isAgentItem(item) && !OB_NOT_ON_HIRE.has(item.status))
    .sort((a, b) => {
      const ka = a.daysLeft == null ? Infinity : a.daysLeft;
      const kb = b.daysLeft == null ? Infinity : b.daysLeft;
      return ka === kb ? a.idx - b.idx : (ka < kb ? -1 : 1);
    })
    .map(({ item, daysLeft }) => ({ item, daysLeft }));
}

/** The subset that needs the hire today: due today or overdue. */
export function obHireNeedsYouToday(instance, now = new Date()) {
  return obHireOpenItems(instance, now).filter((e) => e.daysLeft != null && e.daysLeft <= 0);
}

/* RW-1225: instance.createdBy is a real uid only when a staff member manually
   assigned the checklist. Server-created instances stamp their provisioning
   source string instead (onboardingProvision.js: 'auto-provision' /
   'reconcile' / 'self-service'), so a notification addressed to it reaches
   nobody. Legacy docs may also carry the bare 'admin' fallback. */
const OB_SYSTEM_CREATORS = new Set(['auto-provision', 'reconcile', 'self-service', 'admin']);
export const obCreatorUid = (createdBy) =>
  (createdBy && !OB_SYSTEM_CREATORS.has(createdBy) ? createdBy : null);

const GENERIC_ROLE = {
  agent:       { role: 'Agent',       initials: 'AG', color: OB.coral },
  tc:          { role: 'TC',          initials: 'TC', color: OB.blue },
  trainer:     { role: 'Trainer',     initials: 'TR', color: OB.mossMid },
  super_admin: { role: 'Admin',       initials: 'SA', color: OB.indigo },
};

/** Resolve a role token (or user uid string) to a display descriptor.
 *
 *  RW-1685 (v3 plan §0.2): the LIVE roster wins. For the single-owner staff
 *  tokens (the OB_STAFF keys — never 'agent'/'tc'/'trainer', which are
 *  multi-person), a loaded user actually holding the role token beats the
 *  static map, so the picker can never show a departed staffer while the
 *  roster knows better. Ties prefer a name match to the static record
 *  (resolveConciergeUser precedent), else the first active holder. Static
 *  map → generic role chip → neutral chip remain the fallbacks. */
export function staffForToken(token, allUsers = []) {
  if (!token) return { role: '—', initials: '—', color: OB.mute2 };
  if (OB_STAFF[token]) {
    const named = OB_STAFF[token];
    const holders = (allUsers || []).filter((u) => u && u.isActive !== false
      && normalizeRoles(u.roles != null ? u.roles : u.role).includes(token));
    if (holders.length) {
      const u = holders.find((x) => x.displayName === named.name) || holders[0];
      const nm = u.displayName || u.name || u.email || 'User';
      return {
        name: nm,
        role: named.role,
        initials: nm.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase(),
        color: named.color,
        uid: u.uid || u.id,
        src: u.photoURL || undefined,
      };
    }
    return named;
  }
  if (GENERIC_ROLE[token]) return GENERIC_ROLE[token];
  // Looks like a uid — resolve to a real user if we have one.
  const u = allUsers.find((x) => x.uid === token || x.id === token);
  if (u) {
    const nm = u.displayName || u.name || u.email || 'User';
    return { name: nm, role: u.title || '', initials: nm.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase(), color: OB.mute2 };
  }
  return { role: token, initials: (token[0] || '?').toUpperCase(), color: OB.mute2 };
}

const obKit = { OB, OBIcon, OBEyebrow, OBPill, OBAutoBadge, OBAvatar, OBProgressBar, OBBtn, OBCard, OB_STAFF, OB_PHASE_LABELS, staffForToken };
export default obKit;
